import { z } from 'zod'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import { v4 } from 'uuid'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import type { SendChatNodeData, SidebarComponentProps } from '../types'
import { v2validations } from '../config/validation.config'
import { GenericSidebarComponent } from './sidebar.base'
import { MediaUpload } from '../components/media-upload/media-upload'
import { NodevarList } from '../components/nodevar-list'

type FormValues = SendChatNodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).send_chat
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        type: 'text',
      },
    },
    {
      name: 'chat_content_type',
      label: 'Message Type',
      componentType: 'select',
      zodValidation: validations.shape.chat_content_type,
      inputProps: {
        searchable: false,
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Audio/Video/Document', value: 'chat_media' },
        ],
      },
    },
    {
      name: 'text',
      label: 'Message',
      componentType: 'textarea',
      zodValidation: validations.shape.text,
      onChangeListenTo: ['chat_content_type'],
      visible: (formValues) => formValues.chat_content_type === 'text',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=text]"
            sideOffset={40}
          />
        ),
      },
    },
    {
      name: 'chat_media',
      type: 'custom',
      label: 'Upload media (optional)',
      zodValidation: z.any(),
      visible: (formValues) => formValues.chat_content_type === 'chat_media',
      onChangeListenTo: ['chat_content_type'],
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<SendChatNodeData['config']>
        onChange: (value: Record<string, unknown>) => void
      }) => {
        const onUrlChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFiles = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange(mediaFiles)
        }

        const onRemove = (fileToRemove: RFileUploadResponse) => {
          if (form.state.values.chat_media?.url === fileToRemove.file_url) {
            onChange({})
          }
        }

        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.chat_media?.url) {
          initialFiles = [
            {
              file_type: form.state.values.chat_media.media_type as FileMimeType,
              file_name: form.state.values.chat_media.name || '',
              file_url: form.state.values.chat_media.url || '',
              file_size: form.state.values.chat_media.size || 0,
              file_id: form.state.values.chat_media.file_id || v4(),
            },
          ]
        }
        return (
          <MediaUpload
            onURLChange={onUrlChange}
            label="Upload media (optional)"
            initialFiles={initialFiles}
            multiUpload={false}
            onRemove={onRemove}
            nodeVars={nodeVars}
          />
        )
      },
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const SendChatSidebarComponent = (props: SidebarComponentProps<SendChatNodeData>) => {
  const { nodeVars } = props
  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
